import type { PaymentInfo } from '@/root/shared-types/reservation/reservation-types'

export type FinishRouting =
  | { scenario: 'payment'; toPayAmount: number }
  | { refundAmount: number; scenario: 'refund' }
  | { scenario: 'ticket' }

const getNewRefundOffers = (baseline: PaymentInfo | undefined, committed: PaymentInfo | undefined) => {
  const baselineOffers = baseline?.refundOffers ?? []
  const committedOffers = committed?.refundOffers ?? []
  const baselineOffersHaveIds = baselineOffers.every(({ refundOfferId }) => Boolean(refundOfferId))
  const baselineOfferIds = new Set(
    baselineOffers.flatMap(({ refundOfferId }) => (refundOfferId ? [refundOfferId] : []))
  )

  if (baselineOffersHaveIds) {
    const offersWithNewIds = committedOffers.filter(
      ({ refundOfferId }) => refundOfferId !== undefined && !baselineOfferIds.has(refundOfferId)
    )

    if (offersWithNewIds.length > 0) {
      return offersWithNewIds
    }
  }

  return committedOffers.length > baselineOffers.length ? committedOffers.slice(baselineOffers.length) : []
}

/**
 * Resolves where the customer goes after the reservation modification is finished.
 * The modified reservation identifies the financial result of the current edit.
 * The committed reservation is used only for the final extra-payment check because
 * its refund fields may include earlier unpaid refunds and historical offers.
 */
export const resolveFinishRouting = (
  baseline: PaymentInfo | undefined,
  modified: PaymentInfo | undefined,
  committed: PaymentInfo | undefined = modified
): FinishRouting => {
  const toPayAmount = committed?.toPayAmount ?? 0

  if (toPayAmount > 0) {
    return { scenario: 'payment', toPayAmount }
  }

  const newRefundOffers = getNewRefundOffers(baseline, modified)
  const latestRefundOffer = newRefundOffers.at(-1)
  const toRefundAmount = modified?.toRefundAmount
  const isZeroRefund =
    toRefundAmount === 0 && newRefundOffers.every(({ totalRefundAmount }) => totalRefundAmount === 0)

  if (latestRefundOffer && (toRefundAmount === undefined || toRefundAmount > 0 || isZeroRefund)) {
    return {
      refundAmount: toRefundAmount ?? latestRefundOffer.totalRefundAmount,
      scenario: 'refund',
    }
  }

  return { scenario: 'ticket' }
}
